import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Dialog,
  DialogPopup,
  DialogTitle,
  DND_TOUCH_ACTIVATION,
  Icon,
  ScrollArea,
  SPRING_DROP_ANIMATION,
  Text,
  toastManager,
} from "@salestrak/ui";
import { STAGE_TYPES } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { useStageMutations } from "#app/lib/hooks/use-stage-mutations";
import { useStagesList } from "#app/lib/hooks/use-stages-list";
import {
  DraftGroupSection,
  DraftRowCard,
  STAGE_LIST_MAX_WIDTH,
  StageGroupSkeleton,
} from "./stage-draft-rows";
import { toApplyInput, useStageDrafts, type StageDraft } from "./use-stage-drafts";

// ---------------------------------------------------------------------------
// Dialog shell — the window tier, open/close controlled by the mount
// ---------------------------------------------------------------------------

export function WorkflowStagesDialog({
  workflowId,
  open,
  onClose,
}: {
  workflowId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  // Content registers an interceptor here: dialog-level closes (outside
  // click, Escape, the header ✕) defer to the discard-changes confirm while
  // the draft is dirty.
  const closeGuardRef = useRef<(() => boolean) | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) return;
    if (closeGuardRef.current?.()) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      // The phone sheet: a work surface takes the full height.
      sheetSize="full"
    >
      <DialogPopup
        // bg-background over the stock bg-popover: the dialog body drops to
        // the page fill so the stage card reads as the raised layer.
        // (className = tracked library gap.)
        className="bg-background"
        size="window"
        initialFocus={false}
        showCloseButton={false}
      >
        {/* The content renders its own header (identity + close); this
            satisfies the accessible-name requirement on the dialog popup. */}
        <DialogTitle visuallyHidden>Edit stages</DialogTitle>
        {workflowId ? (
          <WorkflowStagesContent
            workflowId={workflowId}
            onClose={onClose}
            closeGuardRef={closeGuardRef}
          />
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Content — draft state over the fetched list, one Apply commit
// ---------------------------------------------------------------------------
function WorkflowStagesContent({
  workflowId,
  onClose,
  closeGuardRef,
}: {
  workflowId: string;
  onClose: () => void;
  closeGuardRef: React.MutableRefObject<(() => boolean) | null>;
}) {
  const { workflowName, stages, loading, error, refetch } = useStagesList(workflowId);
  const { applyWorkflowStages } = useStageMutations();

  const {
    groups,
    dirty,
    editingKey,
    exitEdit,
    duplicateKeys,
    hasDuplicates,
    hasBlank,
    actions,
    addStage,
    draftByKey,
    reorder,
  } = useStageDrafts({ stages, loading, error });
  const [activeDraft, setActiveDraft] = useState<StageDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  dirtyRef.current = dirty;

  useEffect(() => {
    closeGuardRef.current = () => {
      if (!dirtyRef.current) return false;
      setConfirmDiscardOpen(true);
      return true;
    };
    return () => {
      closeGuardRef.current = null;
    };
  }, [closeGuardRef]);

  const requestClose = useCallback(() => {
    if (dirtyRef.current) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  }, [onClose]);


  // ---- drag (within a category only) ---------------------------------------
  // Capability-based, never viewport-gated: a mouse drags after 8px of
  // travel, a finger after a hold on the grip lane.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      // Grabbing a grip mid-edit leaves edit mode with the typed name kept —
      // the drag takes over seamlessly.
      exitEdit();
      setActiveDraft(draftByKey.get(event.active.id as string) ?? null);
    },
    [draftByKey, exitEdit],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDraft(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      reorder(active.id as string, over.id as string);
    },
    [reorder],
  );

  // ---- apply ----------------------------------------------------------------
  const handleApply = useCallback(async () => {
    if (!groups) return;
    setSubmitting(true);
    try {
      const result = await applyWorkflowStages(workflowId, {
        stages: toApplyInput(groups),
      });
      if (!result) {
        toastManager.add({ title: "Failed to update stages", type: "error" });
        return;
      }
      if (result.__typename !== "ApplyWorkflowStagesSuccess") {
        toastManager.add({
          title: result.message || "Failed to update stages",
          type: "error",
        });
        return;
      }
      onClose();
    } catch (err) {
      toastMutationError(err, "Failed to update stages");
    } finally {
      setSubmitting(false);
    }
  }, [groups, workflowId, applyWorkflowStages, onClose]);


  return (
    <Box fill="flex" overflow="hidden" direction="column">
      {/* The ClickUp-shaped title bar (deliberately not ManageDialogHeader's
          48px identity bar): one prominent sentence naming the workflow being
          edited, and the lone ✕. */}
      <Box
        direction="row"
        align="center"
        justify="between"
        gap="md"
        border="bottom"
        paddingX="lg"
        minHeight={64}
        shrink={false}
      >
        <Text as="h2" size="lg" weight="semibold" truncate>
          Edit{" "}
          {/* The dashed underline marks the one variable word in the title —
              which workflow this is. (decoration classes = tracked gap; the
              Text underline axis is solid-only.) */}
          <Text
            as="span"
            size={null}
            underline
            className="decoration-dashed decoration-muted-foreground"
          >
            {workflowName}
          </Text>{" "}
          stages
        </Text>
        <Button
          aria-label="Close"
          size="icon-sm"
          color="neutral"
          onClick={requestClose}
        >
          <Icon name="x" />
        </Button>
      </Box>

      <Box fill="flex" overflow="hidden" direction="column">
        {error && stages.length === 0 && groups === null ? (
          <LoadErrorState
            noun="stages"
            error={error}
            onRetry={() => {
              void refetch();
            }}
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <ScrollArea>
              <Box align="center" paddingY="xl" paddingX="md">
                <Box fill="width" maxWidth={STAGE_LIST_MAX_WIDTH} gap="xl">
                  {groups === null
                    ? STAGE_TYPES.map((type) => (
                        <StageGroupSkeleton key={type} type={type} />
                      ))
                    : STAGE_TYPES.map((type) => (
                        <DraftGroupSection
                          key={type}
                          type={type}
                          drafts={groups[type]}
                          groupsRef={groups}
                          editingKey={editingKey}
                          duplicateKeys={duplicateKeys}
                          actions={actions}
                          onAddStage={addStage}
                        />
                      ))}
                </Box>
              </Box>
            </ScrollArea>
            {
              // Portalled to the body: DragOverlay positions with fixed
              // viewport coordinates, and the dialog popup's transform would
              // re-root them — rendered inside it, the overlay lands far
              // from the pointer.
              createPortal(
                <DragOverlay dropAnimation={SPRING_DROP_ANIMATION}>
                  {activeDraft ? <DraftRowCard draft={activeDraft} /> : null}
                </DragOverlay>,
                document.body,
              )}
          </DndContext>
        )}
      </Box>

      <Box
        direction="row"
        align="center"
        justify="between"
        gap="md"
        border="top"
        paddingX="md"
        minHeight={56}
        shrink={false}
      >
        <Box shrink>
          {hasDuplicates ? (
            <Text size="sm" variant="destructive">
              Stage names must be unique
            </Text>
          ) : hasBlank ? (
            <Text size="sm" variant="destructive">
              Stage names can&rsquo;t be blank
            </Text>
          ) : dirty ? (
            <Text size="sm" variant="muted">
              Changes aren&rsquo;t saved until you apply them
            </Text>
          ) : null}
        </Box>
        <Box direction="row" align="center" gap="sm" shrink={false}>
          <Button variant="outline" onClick={requestClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!dirty || hasDuplicates || hasBlank || submitting}
            loading={submitting}
          >
            Apply changes
          </Button>
        </Box>
      </Box>

      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              The stage changes you drafted haven&rsquo;t been applied. Closing
              now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Keep editing
            </AlertDialogClose>
            <Button
              color="destructive"
              onClick={() => {
                setConfirmDiscardOpen(false);
                onClose();
              }}
            >
              Discard changes
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}
